import Swal from "sweetalert2";
import type { AxiosError, AxiosRequestConfig, Method } from "axios";
import axios from "axios";
import { firebaseAuth, firebaseStorage } from "plugins/firebase";
import { ref, uploadBytesResumable } from "firebase/storage";

export type ApiErrorBody = { error?: string; message?: string };

export function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error.trim() && !/^\s*<(!doctype|html)/i.test(error)) return error;
  if (error && typeof error === 'object') {
    const body = error as ApiErrorBody;
    if (typeof body.error === 'string') return body.error;
    if (typeof body.message === 'string') return body.message;
  }
  return fallback;
}

// Envuelve el cuerpo de error de la API en un Error real (manteniendo .error/.message
// legibles) para no romper los catch(error) existentes que leen esos campos directamente.
function toRejectionError(data: ApiErrorBody | undefined): Error & ApiErrorBody {
  const err = new Error(data?.error ?? data?.message ?? 'Error en la petición');
  return Object.assign(err, data);
}

const urlBase = import.meta.env.VITE_API_URL || "/api/admin";

const getHeaders = async (baseHeaders?: Record<string, string>): Promise<Record<string, string>> => {
  const DEFAULT_HEADERS: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "content-language": localStorage.getItem("locale") ?? "es",
  };
  const headers = { ...(baseHeaders ?? DEFAULT_HEADERS) };
  await firebaseAuth.authStateReady();
  const currentUser = firebaseAuth.currentUser;
  if (currentUser) {
    try {
      const idToken = await currentUser.getIdToken();
      headers["Authorization"] = `Bearer ${idToken}`;
    } catch {
      // Proceed without auth header if token refresh fails
    }
  }
  return headers;
};

const _http = async <T>(
  method: Method,
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  axiosConfig?: AxiosRequestConfig
): Promise<T> => {
  const newHeaders = await getHeaders(headers);
  return new Promise<T>((resolve, reject) => {
    url = urlBase + url;
    const config: AxiosRequestConfig = {
      method,
      url,
      data,
      headers: newHeaders,
      ...axiosConfig, // Spread any additional axios config (timeout, etc.)
    };

    axios<T>(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error: AxiosError<ApiErrorBody>) => {
        void Swal.fire({
          title: "¡Error!",
          text: getErrorMessage(
            error.response?.data,
            error.response
              ? 'Ocurrió un error al procesar la solicitud'
              : 'No se pudo conectar con el servidor, intente nuevamente',
          ),
          icon: "error",
          confirmButtonText: "Aceptar",
        });
        reject(toRejectionError(error.response?.data));
      });
  });
};

const _httpBlob = async <T>(
  method: Method,
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  responseType: 'json' | 'blob' = 'blob'
): Promise<T> => {
  const newHeaders = await getHeaders(headers);
  return new Promise<T>((resolve, reject) => {
    url = urlBase + url;
    const config: AxiosRequestConfig = {
      method,
      url,
      data,
      headers: newHeaders,
      responseType,
    };

    axios<unknown>(config)
      .then((response) => {
        if (responseType === 'blob') {
          resolve({
            data: response.data,
            headers: response.headers,
          } as T);
        } else {
          resolve(response.data as T);
        }
      })
      .catch((error: AxiosError<ApiErrorBody>) => {
        void Swal.fire({
          title: "¡Error!",
          text: getErrorMessage(
            error.response?.data,
            error.response
              ? 'Ocurrió un error al procesar la solicitud'
              : 'No se pudo conectar con el servidor, intente nuevamente',
          ),
          icon: "error",
          confirmButtonText: "Aceptar",
        });
        reject(toRejectionError(error.response?.data));
      });
  });
};

async function _download(
  method: Method,
  routePath: string,
  data?: unknown,
  filename?: string,
  fileType?: 'EXCEL' | 'PDF' | 'ZIP', // Agregar mas tipos de archivos de ser necesario
): Promise<unknown> {
  try {
    const url = `${urlBase}${routePath}`;
    const baseHeaders =
      fileType === 'EXCEL'
        ? {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'Content-Type': 'application/json',
          }
        : fileType === 'PDF'
        ? {
            Accept: 'application/pdf',
            'Content-Type': 'application/json',
          }
        : fileType === 'ZIP'
        ? {
            Accept: 'application/zip',
            'Content-Type': 'application/json',
          }
        : undefined;
    const response = await fetch(url, {
      method: method.toUpperCase(),
      headers: await getHeaders(baseHeaders),
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response || response.ok === false) {
      let errorMsg = 'Error al descargar el archivo';
      try {
        const errorBody = await response.json() as ApiErrorBody;
        if (errorBody.error) {
          errorMsg = errorBody.error;
        }
      } catch {
        // Si no es JSON, ignorar
      }
      // Mostrar el error
      void Swal.fire({
        title: '¡Error!',
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      throw new Error(errorMsg);
    }

    const resObj = {
      filename:
        filename ||
        fnGetFileNameFromContentDispostionHeader(
          String(response.headers.get('content-disposition')),
        ),
      blob: await response.blob(),
    };
    const newBlob = new Blob([resObj.blob], {
      type: `${response.headers.get('content-type')}`,
    });

    // MS Edge and IE don't allow using a blob object directly as link href, instead it is necessary to use msSaveOrOpenBlob
    const legacyNavigator = window.navigator as Navigator & { msSaveOrOpenBlob?: (blob: Blob) => void };
    if (legacyNavigator.msSaveOrOpenBlob) {
      legacyNavigator.msSaveOrOpenBlob(newBlob);
      return true;
    }

    // For other browsers: create a link pointing to the ObjectURL containing the blob.
    const objUrl = window.URL.createObjectURL(newBlob);

    const link = document.createElement('a');
    link.href = objUrl;
    link.download = resObj.filename;
    link.click();

    // For Firefox it is necessary to delay revoking the ObjectURL.
    setTimeout(() => {
      window.URL.revokeObjectURL(objUrl);
    }, 250);

    return true;
  } catch {
    return false;
  }
}

interface UploadResult {
  error?: string;
  file?: string;
  name?: string;
  msg?: string;
  message?: string;
}

async function _uploadFileToGoogleCloudStorage(file: File, fileName: string, onProgress?: (progress: number, uploaded: boolean) => void): Promise<UploadResult> {
  const FILE = file;
  const FILENAME = fileName;
  return new Promise<UploadResult>((resolve) => {
      const formdata = new FormData();
      formdata.append('fileItem', FILE, FILENAME);
      formdata.append('fileName', FILENAME);
      const storageRef = ref(firebaseStorage, FILENAME);
      const uploadTask = uploadBytesResumable(storageRef, FILE);
      uploadTask.on(
          'state_changed',
          function (snapshot) {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              if (onProgress) onProgress(progress, false);
          },
          function (error) {
              console.error('[firebase] uploadTask ERROR', error);
              return resolve({ error: error.toString() });
          },
          function () {
              _http<UploadResult>('post', '/storage/register', { fileName: FILENAME })
                .then((registerResult) => {
                  if (onProgress) onProgress(100, true);
                  return resolve(registerResult);
                })
                .catch((error: ApiErrorBody) => {
                  return resolve({ error: error.error ?? 'Error al subir el archivo' });
                });
          },
      );
  });
}

function _removeFileFromGoogleCloudStorage(fileName: string): Promise<UploadResult> {
  return _http<UploadResult>('delete', '/storage/upload', { fileName })
}

function fnGetFileNameFromContentDispostionHeader(header: string) {
  const contentDispostion = header ? header.split(';') : [];
  const fileNameToken = `filename=`;
  let filename = 'sample.txt';
  for (const thisValue of contentDispostion) {
      if (thisValue.trim().indexOf(fileNameToken) === 0) {
          filename = decodeURIComponent(thisValue.trim().replace(fileNameToken, ''));
          break;
      }
  }
  return filename;
}

const http = {
  get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    return _http<T>("get", url, undefined, headers);
  },

  post<T, B = Record<string, unknown>>(url: string, data: B, headers?: Record<string, string>, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return _http<T>("post", url, data, headers, axiosConfig);
  },

  put<T, B = Record<string, unknown>>(url: string, data: B, headers?: Record<string, string>): Promise<T> {
    return _http<T>("put", url, data, headers);
  },

  patch<T, B = Record<string, unknown>>(url: string, data: B): Promise<T> {
    return _http<T>("patch", url, data);
  },

  delete<T = void>(url: string, data?: unknown): Promise<T> {
    return _http<T>("delete", url, data);
  },

  download(url: string, filename: string, filetype?: "EXCEL" | "ZIP") {
    return _download('get', url, null, filename, filetype);
  },
  uploadFile(file: File, fileName: string, onProgress?: (progress: number, uploaded: boolean) => void) {
    return _uploadFileToGoogleCloudStorage(file, fileName, onProgress);
  },
  removeFile(fileName: string) {
    return _removeFileFromGoogleCloudStorage(fileName);
  }
};

export const httpBlob = {
  get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    return _httpBlob<T>('get', url, undefined, headers);
  }
}

export default http;
